import clsx from "clsx";
import { useId, useRef, type ReactNode } from "react";

export interface TabItem {
  id: string;
  label: ReactNode;
  content?: ReactNode;
}

export interface TabsProps {
  items: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
  ariaLabel?: string;
}

/** Controlled tabs with arrow-key navigation (WAI-ARIA tabs pattern). */
export function Tabs({ items, value, onChange, className, ariaLabel }: TabsProps) {
  const base = useId();
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeIndex = Math.max(
    0,
    items.findIndex((t) => t.id === value),
  );
  const active = items[activeIndex];

  const move = (delta: number) => {
    const next = items[(activeIndex + delta + items.length) % items.length];
    if (!next) return;
    onChange(next.id);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${base}-${next.id}`)}`)
      ?.focus();
  };

  return (
    <div className={className}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        className="flex gap-1 border-b border-ink-200 dark:border-ink-800"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            move(1);
          } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        {items.map((item) => {
          const selected = item.id === active?.id;
          return (
            <button
              key={item.id}
              id={`${base}-${item.id}`}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={item.content !== undefined ? `${base}-${item.id}-panel` : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(item.id)}
              className={clsx(
                "-mb-px rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                selected
                  ? "border-accent-600 text-accent-800 dark:text-accent-200"
                  : "border-transparent text-ink-600 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {active?.content !== undefined && (
        <div
          id={`${base}-${active.id}-panel`}
          role="tabpanel"
          aria-labelledby={`${base}-${active.id}`}
          className="pt-4"
        >
          {active.content}
        </div>
      )}
    </div>
  );
}
